import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { CashStack } from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { QRCodeCanvas } from "qrcode.react";
import PPIService from "../../services/PPIService";

const PlatformFeesDialog = ({ show, onHide, fees }) => {
  const [orderId, setOrderId] = useState("");
  const [upiUrl, setUpiUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180);

  const timerRef = useRef(null);
  const intervalRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setOrderId("");
      setUpiUrl("");
      setTimeLeft(180);
      clearInterval(timerRef.current);
      clearInterval(intervalRef.current);
    }

    return () => {
      clearInterval(timerRef.current);
      clearInterval(intervalRef.current);
    };
  }, [show]);

  const formatTime = (secs) => {
    const minutes = Math.floor(secs / 60);
    const seconds = secs % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const handleInitiateFeesPayment = async () => {
    setLoading(true);
    try {
      const result = await PPIService.initiatePlatformFeesPayment();

      if (!result.success) {
        return Swal.fire("Oops!", result.message || "Failed to fetch", "error");
      }

      const data = result.data?.data;

      if (result.data.status === "SUCCESS" && data) {
        setUpiUrl(data.upi_intent);
        setOrderId(data.order_id);
        setTimeLeft(180);

        // Start QR timer
        timerRef.current = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              clearInterval(intervalRef.current);
              setUpiUrl("");
              setOrderId("");
              Swal.fire(
                "QR Expired",
                "Please try again to get a new QR code",
                "info"
              );
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        Swal.fire("Oops!", result.data.message || "Payment failed", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error?.response?.data?.message || error.message || "Request failed",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const txnStatus = async () => {
    if (!orderId) return;
    try {
      const result = await PPIService.cardReloadStatus(orderId);

      if (result.success && result.data.status === "SUCCESS") {
        Swal.fire("Success!", "Payment successful!", "success");
        closeHandler();
      }
    } catch (error) {
      console.error("Transaction status check failed:", error);
    }
  };

  // Polling for transaction status
  useEffect(() => {
    if (upiUrl && orderId) {
      intervalRef.current = setInterval(() => {
        txnStatus();
      }, 15000);
    }
    return () => clearInterval(intervalRef.current);
  }, [upiUrl, orderId]);

  const closeHandler = () => {
    clearInterval(timerRef.current);
    clearInterval(intervalRef.current);
    setUpiUrl("");
    setOrderId("");
    setTimeLeft(180);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={closeHandler}
      centered
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title className="d-flex align-items-center">
          <CashStack className="me-2" size={22} />{" "}
          {upiUrl
            ? "Pay Platform Upgrade Fee"
            : "Platform Fee for Unlocking Premium Features"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4">
        {!upiUrl ? (
          <>
            <div className="text-center mb-3">
              <h4 className="fw-bold text-success">₹{fees}</h4>
              <p className="mb-2 fw-medium">
                One-time platform upgrade to unlock premium features.
              </p>
              <p className="text-muted small mb-0">
                After upgrading, you can proceed with Full KYC and access
                enhanced limits.
              </p>
            </div>

            <div className="border-top pt-3">
              <ul className="list-unstyled mb-0">
                <li>✅ Unlock premium account features</li>
                <li>✅ Enable Full KYC process</li>
                <li>✅ Increase wallet limit up to ₹2,00,000/month</li>
                <li>✅ Enjoy faster processing & support</li>
              </ul>
            </div>

            <p className="text-muted small mt-3">
              *This fee is not charged for KYC. It is for upgrading your account
              platform and services.
            </p>
          </>
        ) : (
          <div className="text-center mb-3">
            <h6 className="fw-semibold mb-3">
              Scan this QR code to Pay Platform Upgrade Fee
            </h6>
            <QRCodeCanvas value={upiUrl} size={200} />
            <p className="mt-2 text-danger fw-bold">
              QR expires in: {formatTime(timeLeft)}
            </p>
            <p className="text-muted small">Scan & Pay with any BHIM UPI App</p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-between px-4 pb-4">
        <Button
          variant="outline-secondary"
          onClick={closeHandler}
          disabled={loading}
        >
          Cancel
        </Button>

        {!upiUrl && (
          <Button
            variant="success"
            onClick={handleInitiateFeesPayment}
            disabled={loading}
          >
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Proceed to Pay"
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PlatformFeesDialog;
